// THE SHOP DRAWING FILTERS, ONE BAR FOR EVERY PAGE THAT FILTERS BY THEM
// (owner, 2026-10-23: Document Control gets "all the shopdrawings
// columns/properties" as filters). Moved out of ShopDrawingsPage word for
// word, so the register and Document Control cannot drift apart: the same
// controls, the same filter object, the same saved filters (one preference,
// shopDrawings.savedFilters), answered by the same server query
// (routes/shopDrawings resolveFilters + tableFor).
import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Input, Select, Tooltip, message } from "antd";
import moment from "moment";
import ActivityFilterSelect from "components/Coordination/ActivityFilterSelect";
import OwnerSignOffFilter from "components/Coordination/OwnerSignOffFilter";
import { usePreference } from "helpers/userPrefs";
import { getShopDrawingAreaSignOff, shopDrawingFilterQuery } from "api/tracking/shopDrawingFilterApi";

// WHICH DATE THE RANGE PICKER IS ABOUT. One picker and a chooser rather
// than three pickers: the owner asked for the issues list's shape, and
// three ranges side by side is the thing people mis-read there.
const DATE_FIELDS = [
  { value: "requested", label: "Requested on" },
  { value: "delivered", label: "Delivered on" },
  { value: "signOff", label: "Signed off on" },
  { value: "due", label: "Due on" },
];
const WINDOW_KEYS = {
  requested: ["requestedFrom", "requestedTo"],
  delivered: ["deliveredFrom", "deliveredTo"],
  signOff: ["signOffFrom", "signOffTo"],
  due: ["dueFrom", "dueTo"],
};
// Every window key emptied, so changing WHICH date cannot leave the old
// one applied - a filter still narrowing after its control has moved on is
// the shape nobody can see and everybody blames on the data.
const EMPTY_WINDOWS = Object.values(WINDOW_KEYS).flat()
  .reduce((a, k) => ({ ...a, [k]: "" }), {});
const ymd = (m) => (m && m.format ? m.format("YYYY-MM-DD") : "");
function windowPatch(field, range) {
  const [from, to] = WINDOW_KEYS[field] || WINDOW_KEYS.requested;
  return { ...EMPTY_WINDOWS, dateField: field, [from]: ymd(range && range[0]), [to]: ymd(range && range[1]) };
}
function windowValue(filters) {
  const [from, to] = WINDOW_KEYS[filters.dateField || "requested"] || [];
  const at = (v) => (v ? moment(v, "YYYY-MM-DD") : null);
  return [at(filters[from]), at(filters[to])];
}
// THE THREE STATES A PERSON ACTUALLY ASKS FOR, as one control: they are
// not independent checkboxes, and offering them as three invites
// "overdue AND signed off", which is nothing.
const STATE_OPTIONS = [
  { value: "overdue", label: "Overdue" },
  { value: "delivered", label: "Delivered" },
  { value: "undelivered", label: "Not delivered" },
  { value: "signed", label: "Signed off" },
  { value: "unsigned", label: "Not signed off" },
];
const STATE_PATCH = {
  overdue: { overdue: "true" },
  delivered: { delivered: "true" },
  undelivered: { delivered: "false" },
  signed: { signedOff: "true" },
  unsigned: { signedOff: "false" },
};
const EMPTY_STATES = { overdue: "", delivered: "", signedOff: "" };
function stateValue(f) {
  if (f.overdue === "true") return "overdue";
  if (f.delivered === "true") return "delivered";
  if (f.delivered === "false") return "undelivered";
  if (f.signedOff === "true") return "signed";
  if (f.signedOff === "false") return "unsigned";
  return undefined;
}

// RELEASED, NOT "SIGNED OFF" (owner, 2026-10-09: "status, should say released
// not signedoff, signed off is only for last revision, on a closing/signedoff
// area").
//
// SIGN-OFF IS NOT A DRAWING STATE AT ALL under his model: it belongs to the
// AREA, it happens on the FINAL revision, and it happens when the area is
// being closed. A drawing that has been validated and sent is RELEASED.
//
// The mail already had this right - it says "this does not sign off the
// schedule activity on its own - an area is signed off once every sheet it
// needs is in" - and the table was the surface still disagreeing with it.
export const STATUS_LABEL = {
  requested: "Requested", submitted: "Delivered", under_review: "Waiting for approval",
  approved: "Released", rejected: "Returned",
};

// The schedule stages an area's sign-off can be read from (utils/systemTasks).
const SIGN_OFF_STAGES = [
  { value: "modeling", label: "Modeling" },
  { value: "coordination", label: "Coordination" },
  { value: "shop-drawings", label: "Shop drawings" },
  { value: "install", label: "Install" },
];

// The Area sign-off choices with their counts, and where the sign-off dates
// come from (owner, 2026-10-16). Built here, not inline: the toolbar is long
// enough that one more branch in the component trips the hooks linter.
function signOffStateOptions(so) {
  const n = (k) => (so && so.counts && so.counts[k] != null ? ` (${so.counts[k]})` : "");
  return [
    { value: "signed", label: `Signed off${n("signed")}` },
    { value: "after", label: `Created after sign-off${n("after")}` },
    { value: "open", label: `Not signed off${n("open")}` },
    { value: "none", label: `No area${n("none")}` },
  ];
}

const SIGN_OFF_SOURCES = [
  { value: "schedule", label: "Old schedule" },
  { value: "simulation", label: "New register" },
  { value: "both", label: "Both (register wins)" },
];

/** The filter object and everything the bar needs beside it. An emptied
 *  control is REMOVED, not kept as "": an empty value in the query would be
 *  a filter the server has to guess the meaning of. */
export function useShopDrawingFilters(projectId) {
  const [filters, setFilters] = useState({});
  const setFilter = useCallback((patch) => setFilters((prev) => {
    const next = { ...prev, ...patch };
    for (const k of Object.keys(next)) {
      const v = next[k];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) delete next[k];
    }
    return next;
  }), []);
  const anyFilter = Object.keys(filters).length > 0;
  // The area sign-off counts the Area sign-off choices show.
  const [drawingSignOff, setDrawingSignOff] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!projectId) { setDrawingSignOff(null); return undefined; }
    Promise.resolve()
      .then(() => getShopDrawingAreaSignOff(projectId, { areaStage: filters.areaStage || "shop-drawings", areaSignOffAsOf: filters.areaSignOffAsOf, areaSignOffSource: filters.areaSignOffSource }))
      .then((r) => { if (alive) setDrawingSignOff(r && r.counts ? r : null); })
      .catch(() => { if (alive) setDrawingSignOff(null); });
    return () => { alive = false; };
  }, [projectId, filters.areaStage, filters.areaSignOffAsOf, filters.areaSignOffSource]);
  // Saved filters are the person's own, stored with the table's exact query
  // so the cost page can apply them.
  const [savedDrawingFilters, setSavedDrawingFilters] = usePreference("shopDrawings.savedFilters", []);
  const saveDrawingFilter = () => {
    const name = window.prompt("Name this filter:");
    if (!name || !name.trim()) return;
    const preset = {
      name: name.trim(), filters, projectId,
      query: shopDrawingFilterQuery({ ...filters, tzOffset: new Date().getTimezoneOffset() }),
    };
    setSavedDrawingFilters([...(savedDrawingFilters || []).filter((f) => f.name !== preset.name), preset]);
    message.success(`Saved "${preset.name}" - it is also offered on the contractor cost page`);
  };
  const applyDrawingFilter = (name) => {
    const f = (savedDrawingFilters || []).find((x) => x.name === name);
    if (f) setFilters({ ...(f.filters || {}) });
  };
  return {
    filters, setFilters, setFilter, anyFilter, drawingSignOff,
    savedDrawingFilters, setSavedDrawingFilters, saveDrawingFilter, applyDrawingFilter,
  };
}

/** The bar. `facets` = { areas, packages, levels, contractors? } the pickers
 *  offer (contractors: [{ companyId, companyName }], the team's only);
 *  `countText` is the page's own "N match" line. */
export default function ShopDrawingFilterBar({ projectId, sf, facets, showDeliveredTo, countText }) {
  const {
    filters, setFilters, setFilter, anyFilter, drawingSignOff,
    savedDrawingFilters, setSavedDrawingFilters, saveDrawingFilter, applyDrawingFilter,
  } = sf;
  return (
    projectId ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "4px 0 10px" }}>
        <Input.Search
          allowClear
          placeholder="Search any column - words in any order, best match first"
          style={{ width: 340 }}
          value={filters.search || ""}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        <Select
          allowClear mode="multiple" maxTagCount="responsive" placeholder="Status"
          style={{ minWidth: 170 }} value={filters.status ? String(filters.status).split(",") : []}
          onChange={(v) => setFilter({ status: v })}
          options={Object.keys(STATUS_LABEL).map((k) => ({ value: k, label: STATUS_LABEL[k] }))}
        />
        <Select
          allowClear mode="multiple" maxTagCount="responsive" placeholder="Area"
          style={{ minWidth: 140 }} value={filters.area ? String(filters.area).split(",") : []}
          onChange={(v) => setFilter({ area: v })}
          options={facets.areas.map((a) => ({ value: a, label: a }))}
        />
        <Select
          allowClear mode="multiple" maxTagCount="responsive" placeholder="Package"
          style={{ minWidth: 150 }} value={filters.package ? String(filters.package).split(",") : []}
          onChange={(v) => setFilter({ package: v })}
          options={facets.packages.map((a) => ({ value: a, label: a }))}
        />
        <Input
          allowClear placeholder="Requested by" style={{ width: 150 }}
          value={filters.requestedBy || ""}
          onChange={(e) => setFilter({ requestedBy: e.target.value })}
        />
        <Input
          allowClear placeholder="Produced by" style={{ width: 150 }}
          value={filters.producedBy || ""}
          onChange={(e) => setFilter({ producedBy: e.target.value })}
        />
        {showDeliveredTo ? (
          <Input
            allowClear placeholder="Delivered to (email)" style={{ width: 190 }}
            value={filters.deliveredToWho || ""}
            onChange={(e) => setFilter({ deliveredToWho: e.target.value })}
          />
        ) : null}
        <Select
          allowClear placeholder="Dated by…" style={{ width: 150 }}
          value={filters.dateField || "requested"}
          onChange={(v) => setFilter({ dateField: v, ...EMPTY_WINDOWS })}
          options={DATE_FIELDS}
        />
        <DatePicker.RangePicker
          allowEmpty={[true, true]}
          value={windowValue(filters)}
          onChange={(v) => setFilter(windowPatch(filters.dateField || "requested", v))}
        />
        <Select
          allowClear placeholder="State" style={{ width: 160 }}
          value={stateValue(filters)}
          onChange={(v) => setFilter({ ...EMPTY_STATES, ...(v ? STATE_PATCH[v] : {}) })}
          options={STATE_OPTIONS}
        />
        {/* THE ACTIVITY, at any level, with everything below it (owner,
            2026-10-15) - the server widens the pick. */}
        <ActivityFilterSelect
          projectId={projectId}
          value={filters.ganttItemId || ""}
          onChange={(v) => setFilter({ ganttItemId: v })}
        />
        <Select
          allowClear mode="multiple" maxTagCount="responsive" placeholder="Level"
          aria-label="Level filter"
          style={{ minWidth: 120 }}
          value={filters.level ? String(filters.level).split(",") : []}
          onChange={(v) => setFilter({ level: v })}
          options={facets.levels.map((a) => ({ value: a, label: a }))}
        />
        {/* THE CONTRACTOR, one or several (owner, 2026-10-23: as on the issue
            export) - the drawings any picked company worked on: its hours,
            a responsible, or who produced it. The export then has a table
            for each picked contractor and no other. */}
        {(facets.contractors || []).length ? (
          <Select
            allowClear mode="multiple" maxTagCount="responsive" placeholder="Contractor"
            aria-label="Contractor filter" optionFilterProp="label"
            style={{ minWidth: 170 }}
            value={filters.contractor ? String(filters.contractor).split(",") : []}
            onChange={(v) => setFilter({ contractor: v })}
            options={facets.contractors.map((c) => ({ value: String(c.companyId), label: c.companyName || String(c.companyId) }))}
          />
        ) : null}
        {/* AREA SIGN-OFF: the drawing's area, judged by the area's
            activity sign-off for a stage by a day. "No area" is its own
            answer - a drawing nobody could place was never checked. */}
        <Tooltip title={drawingSignOff ? (
          <div style={{ fontSize: 12 }}>
            <div style={{ marginBottom: 4 }}>
              A drawing is placed by its area, its area name or its activity. Signed off = the area's {drawingSignOff.stageLabel} activity on the drawing's level is signed off by {drawingSignOff.asOf}.
            </div>
            {(drawingSignOff.areas || []).map((a) => (
              <div key={a.id}><b>{a.name}</b> ({a.issues}): {a.levels.map((l) => `${l.level || "all levels"} ${l.signed ? `signed ${String(l.signedOffAt || "").slice(0, 10)}` : "open"}`).join(", ")}</div>
            ))}
          </div>
        ) : "Drawings whose area is signed off by the schedule's activity sign-offs"}>
          <Select
            allowClear mode="multiple" maxTagCount="responsive" placeholder="Area sign-off"
            aria-label="Area sign-off filter"
            style={{ minWidth: 180 }}
            value={filters.areaSignOff ? String(filters.areaSignOff).split(",") : []}
            onChange={(v) => setFilter({ areaSignOff: v, ...(v && v.length ? {} : { areaStage: "", areaSignOffAsOf: "", areaSignOffSource: "" }) })}
            options={signOffStateOptions(drawingSignOff)}
          />
        </Tooltip>
        <OwnerSignOffFilter
          value={filters.ownerSignOff ? String(filters.ownerSignOff) : ""}
          onChange={(v) => setFilter({ ownerSignOff: v ? v.split(",") : [] })}
        />
        {filters.areaSignOff ? (
          <>
            <Select
              aria-label="Area sign-off stage" style={{ width: 150 }}
              value={filters.areaStage || "shop-drawings"}
              onChange={(v) => setFilter({ areaStage: v === "shop-drawings" ? "" : v })}
              options={SIGN_OFF_STAGES}
            />
            {/* Where the sign-off dates come from (owner, 2026-10-16: "so i can use both new and old schedule"). */}
            <Select
              aria-label="Sign-off dates from" style={{ width: 190 }}
              title="Sign-off dates from: the schedule's own sign-offs (old), the latest simulated schedule from the sign-off register (new), or both"
              value={filters.areaSignOffSource || "schedule"}
              onChange={(v) => setFilter({ areaSignOffSource: v === "schedule" ? "" : v })}
              options={SIGN_OFF_SOURCES}
            />
            <DatePicker
              aria-label="Area signed off by" placeholder="Signed off by: today" allowClear style={{ width: 170 }}
              value={filters.areaSignOffAsOf ? moment(filters.areaSignOffAsOf, "YYYY-MM-DD") : null}
              onChange={(d) => setFilter({ areaSignOffAsOf: d ? d.format("YYYY-MM-DD") : "" })}
            />
          </>
        ) : null}
        <Select
          allowClear placeholder="Saved filters" style={{ width: 160 }} value={undefined}
          aria-label="Saved filters"
          onChange={(name) => name && applyDrawingFilter(name)}
          options={(savedDrawingFilters || []).map((f) => ({ value: f.name, label: f.name }))}
          dropdownRender={(menu) => (
            <>
              {menu}
              <div style={{ borderTop: "1px solid #eaecf0", padding: 6, display: "flex", gap: 8 }}>
                <Button size="small" type="link" disabled={!anyFilter} onClick={saveDrawingFilter}>+ Save current</Button>
                {(savedDrawingFilters || []).length ? (
                  <Button size="small" type="link" danger onClick={() => setSavedDrawingFilters([])}>Clear all</Button>
                ) : null}
              </div>
            </>
          )}
        />
        {anyFilter ? (
          <Button size="small" onClick={() => setFilters({})}>Clear filters</Button>
        ) : null}
        <span style={{ color: "#667085", fontSize: 12, marginLeft: "auto" }}>{countText}</span>
      </div>
    ) : null
  );
}
